import { useState, useCallback } from "react";
import PropTypes from "prop-types";
import { TextField } from "@mui/material";
import { LanguageItem } from "../common";
import useForm from "../common/AsyncFormBox/useForm";
import useFormValidation from "../common/AsyncFormBox/useFormValidation";
import { validateIdentity, stripWhitespace } from "../../config/IdentityFormat";

/**
 * Text input for username (email) with validation matching backend IdentityFormat.
 * Does not allow typing whitespace. Integrates with AsyncFormBox via useFormValidation.
 *
 * @param {Object} props
 * @param {string} [props.name="email"] - Form field name.
 * @param {React.ReactNode} [props.label] - Field label.
 * @param {string} [props.autoComplete="email"] - Autocomplete attribute.
 * @param {boolean} [props.required=true] - Whether field is required.
 * @param {string} [props.errorMessage] - Custom error message when invalid (default: translated).
 * @param {Object} props - Remaining props spread to TextField (margin, fullWidth, autoFocus, etc.).
 */
function UsernameInput({
  name = "email",
  label,
  autoComplete = "email",
  required = true,
  errorMessage,
  defaultValue = "",
  onChange,
  ...rest
}) {
  const [value, setValue] = useState(() => stripWhitespace(defaultValue));
  const [error, setError] = useState(null);
  const form = useForm();

  function handleChange(event) {
    const stripped = stripWhitespace(event.target.value);
    setValue(stripped);
    form.setValue?.(name, stripped);
    event.target.value = stripped;
    onChange?.(event);
  }

  const validate = useCallback(
    (formValues) => {
      const val = formValues[name];
      const stripped = stripWhitespace(typeof val === "string" ? val : "");
      const isEmpty = !stripped;

      if (isEmpty && required) {
        setError(
          <>
            <LanguageItem cs>E-mailová adresa je povinná.</LanguageItem>
            {/* <LanguageItem en>Email address is required.</LanguageItem> */}
          </>,
        );
        return false;
      }
      if (!isEmpty && !validateIdentity(stripped)) {
        setError(
          errorMessage ?? (
            <>
              <LanguageItem cs>Zadejte platnou e-mailovou adresu.</LanguageItem>
              {/* <LanguageItem en>Enter a valid email address.</LanguageItem> */}
            </>
          ),
        );
        return false;
      }
      setError(null);
      return true;
    },
    [name, required, errorMessage],
  );

  useFormValidation(name, validate, []);

  const defaultLabel = (
    <>
      <LanguageItem cs>E-mailová adresa</LanguageItem>
      {/* <LanguageItem en>E-mail address</LanguageItem> */}
    </>
  );

  return (
    <TextField
      {...rest}
      name={name}
      value={value}
      onChange={handleChange}
      label={label ?? defaultLabel}
      autoComplete={autoComplete}
      required={required}
      error={!!error}
      helperText={error}
    />
  );
}

UsernameInput.propTypes = {
  name: PropTypes.string,
  label: PropTypes.node,
  autoComplete: PropTypes.string,
  required: PropTypes.bool,
  errorMessage: PropTypes.node,
  defaultValue: PropTypes.string,
  onChange: PropTypes.func,
};

export default UsernameInput;
